/** Surfaces a disagreement between the contract type the uploader declared and
 *  the one the Document Agent inferred (INV-TYPE-01).
 *
 *  The declared type still drives the pipeline — this is a signal to the
 *  reviewer, not an override. It also locks approval to a senior reviewer,
 *  because acting on the wrong type means the wrong gate rules ran.
 */
import { TriangleAlert } from 'lucide-react';
import { getContractTypeSchema } from '@/pages/contracts/contractTypeSchema';
import type { JurisdictionContext } from './LegalBasisPanel';

export function TypeMismatchCallout({
  context,
}: {
  context: JurisdictionContext | null | undefined;
}) {
  if (!context?.type_mismatch) return null;

  const declared = getContractTypeSchema(context.declared_contract_type).label;
  const detected = getContractTypeSchema(context.detected_contract_type).label;

  return (
    <div className="flex items-start gap-2 rounded border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-800 dark:text-amber-300">
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <div className="space-y-1">
        <p className="font-medium">Declared contract type does not match the document</p>
        <p>
          This was submitted as <strong>{declared}</strong>, but the document reads as{' '}
          <strong>{detected}</strong>. The pipeline used the declared type, so the gate rules,
          expected clauses, and legal bases above are those for {declared}. Approval requires a
          senior reviewer.
        </p>
      </div>
    </div>
  );
}
